/**
 * THE DOCUMENTS A PROJECT IS DELIVERED AGAINST — #193.
 *
 * The words the screen uses, and the small questions asked about a document in
 * more than one place. Kept out of the component for the reason the rest of
 * this folder is: a rule the screen owns is a rule nothing can test on its own.
 */
import type { Doc, DocumentKind, DocumentState } from './types';

/** What each kind is called on screen. The stored value is never shown. */
export const DOCUMENT_KIND: Record<DocumentKind, string> = {
  requirements: 'Requirements',
  change: 'Change request',
  /* BESIDE THE CHANGE REQUEST, not beside the requirements. All three ask for
     work after the requirements were agreed; what separates them is what is
     being asked for. A change alters what was agreed, a feature adds what was
     never in it, an improvement betters something already delivered. */
  feature: 'New feature',
  improvement: 'Improvement',
  test: 'Test script',
  /* The completion sign-off for a sprint — what was delivered. The change
     request authorises the work; this records that it was done. See #0079. */
  deliverables: 'Deliverables',
  /* A progress report sent up — where the project stands this period — routed
     for sign-off and filed like any other produced document. See migration 0156. */
  statusreport: 'Status report',
  /* The closing retrospective — assembled from the board, then filed like any
     other produced document. See migration 0174 and aar.ts. */
  aar: 'After-action report',
  /* The founding document of a project — authored in-app or uploaded, then
     signed like any other. See migration 0102. */
  charter: 'Project charter',
  /* RECEIVED / REFERENCE — handed to the project, kept and read, never signed
     here. See migration 0111 and `isReceived` below. The kind's slug is `sow`
     (statement of work); the label is the recognised contract term — a Statement
     of Work is the document a vendor issues, where a "scope of work" is a section
     within one (Vinnie, 16 Sep: use the known term). */
  sow: 'Statement of work',
  plan: 'Project plan',
  acceptance: 'Acceptance',
  attachment: 'Attachment',
};

/**
 * A ONE-LINE DESCRIPTION OF EACH KIND — shown under its name in the New-document
 * picker (#5) so a person knows what each produces without knowing the vocabulary
 * first. Kept beside the names so the two never drift. Reviewed by the
 * copy-witness for professional, plain, accurate wording (16 Sep 2026).
 */
export const DOCUMENT_KIND_WHAT: Record<DocumentKind, string> = {
  requirements: 'What the release must deliver, each marked must, should, could or won’t this time.',
  change: 'A requested change to agreed scope, schedule, cost or risk.',
  feature: 'Work that was not in the agreed scope.',
  improvement: 'An enhancement to something already delivered.',
  deliverables: 'What a completed sprint delivered, for sign-off.',
  statusreport: 'Where the project stands this period, for sign-off — uploaded or exported from the Portfolio pack.',
  aar: 'A closing retrospective, assembled from the board — what was delivered, what moved and why, and what to carry forward.',
  test: 'The tests to run when a sprint is complete.',
  sow: "A vendor's formal statement of the work and its terms.",
  plan: 'A schedule or plan provided to the project.',
  acceptance: 'A signed acceptance returned by the customer.',
  attachment: 'A supporting file — a diagram, a bill of materials, a reference.',
  charter: 'Sponsor, objectives, scope, stakeholders and assumptions. Opens the charter editor.',
};

/**
 * THE NEW-DOCUMENT PICKER'S GROUPS — #5, in the order they are shown, arranged by
 * what a person is actually doing rather than as a flat list of ten types. Each
 * group names a real document category (never a description of a situation) and
 * says what it is. Charter is reachable here now (it never was before), on its own
 * because it is one per project and covers the whole of it. Every DocumentKind
 * appears in exactly one group — a pure test holds that true.
 */
export const DOCUMENT_GROUPS: { label: string; what: string; kinds: DocumentKind[] }[] = [
  {
    label: 'Documents you produce',
    what: 'Produced for this project and signed off. Some are assembled from your backlog; others you upload — each type says which below.',
    kinds: ['requirements', 'change', 'feature', 'improvement', 'deliverables', 'test', 'statusreport', 'aar'],
  },
  {
    label: 'Documents you receive',
    what: 'Provided to the project and kept for reference. Read here, not sent for signature.',
    kinds: ['sow', 'plan', 'acceptance', 'attachment'],
  },
  {
    label: 'Project initiation',
    what: 'The founding document of the project. One per project, covering all of it.',
    kinds: ['charter'],
  },
];

/**
 * WHETHER A DOCUMENT OF THIS KIND CAN BE SCOPED TO A SPRINT, or only ever covers
 * the whole project. A produced sign-off document can attach to a sprint (a
 * sprint's deliverables, its test script, a change raised within it); the
 * requirements are the release's and the received/charter documents are the
 * project's, so those are whole-project only and the picker shows no sprint choice.
 */
export const canScopeToSprint = (kind: DocumentKind): boolean =>
  kind === 'change' ||
  kind === 'feature' ||
  kind === 'improvement' ||
  kind === 'deliverables' ||
  kind === 'test';

/**
 * HOW A DOCUMENT OF THIS KIND COMES TO EXIST — the picker's per-type tag.
 *
 * The "Documents you produce" group gathers types that read as one family but
 * do not behave as one: some the app ASSEMBLES from your backlog and charter
 * ('generated'), others you write elsewhere and UPLOAD ('uploaded'). Without
 * saying which, the group's one line promises "assembled from your backlog" for
 * all of them, and a person picks Improvement expecting it to build itself. The
 * tag says it on the type, so the expectation is set before the click.
 *
 * `null` is deliberate, not a gap. The received kinds are already named by their
 * group ("Documents you receive"), and the charter carries its own state chip
 * ("Not started" / "Already created"), so a tag on either would only repeat what
 * is already said. Only the produce group's six, where the ambiguity lives, are
 * tagged. The charter is 'generated' in truth, but it is tagged by its own chip,
 * not here.
 */
export const docSource = (kind: DocumentKind): 'generated' | 'uploaded' | null => {
  if (kind === 'requirements' || kind === 'change' || kind === 'deliverables' || kind === 'aar') return 'generated';
  if (kind === 'feature' || kind === 'improvement' || kind === 'test' || kind === 'statusreport') return 'uploaded';
  return null;
};

/** The title the picker suggests for a kind — only the charter gets one. */
export const SUGGESTED_TITLE: Partial<Record<DocumentKind, string>> = {
  charter: 'Project charter',
  aar: 'After-action report',
};

/**
 * THE TITLE BOX'S EXAMPLE, PER KIND — the placeholder shown while the box is empty.
 *
 * A single Requirements example ("Business requirements — release 2.0") stood over
 * every type, so a Test script or a Change request prompted you with a wrong-shaped
 * example (interaction witness, 18 Oct). Each kind now shows an example of its own
 * shape. These are illustrations, never names of real people or companies.
 */
export const TITLE_EXAMPLE: Record<DocumentKind, string> = {
  requirements: 'Business requirements — release 2.0',
  change: 'Change request — extend the delivery window',
  feature: 'New feature — bulk export',
  improvement: 'Improvement — faster board load',
  test: 'Test script — checkout sign-off',
  deliverables: 'Deliverables — Sprint 8',
  statusreport: 'Status report — October',
  aar: 'After-action report',
  charter: 'Project charter',
  sow: 'Statement of work — platform build',
  plan: 'Project plan — v1',
  acceptance: 'Acceptance — Phase 1',
  attachment: 'Network diagram',
};

/**
 * WHAT THE TITLE BOX SHOULD HOLD WHEN THE PICKED TYPE CHANGES.
 *
 * The charter suggests its own name so a person is not made to type it; every
 * other type is named by the person. The rule that keeps the box honest: a title
 * WE suggested is taken back the moment they move to a type that does not suggest
 * it, so "Project charter" never lingers over a requirements document (Vinnie,
 * 16 September). A title the person TYPED is theirs, and is kept across the
 * change — losing typed input on a mis-click is its own bug. `auto` says which of
 * the two the current title is; this returns the next title and the next `auto`.
 */
export function titleForKind(
  next: DocumentKind,
  current: string,
  wasAuto: boolean
): { title: string; auto: boolean } {
  const suggestion = SUGGESTED_TITLE[next];
  if (suggestion) {
    // A type that suggests a title: fill the box only when it is empty, so a
    // typed title (or an existing suggestion) is never overwritten.
    return current.trim() ? { title: current, auto: wasAuto } : { title: suggestion, auto: true };
  }
  // A type that suggests nothing: drop a suggestion we made, keep a typed title.
  return wasAuto ? { title: '', auto: false } : { title: current, auto: wasAuto };
}

/**
 * THE DOCUMENTS A PROJECT IS HANDED, as opposed to the ones it produces.
 *
 * A received document is kept for the record and referred back to — a scope of
 * work someone else wrote, the project plan or schedule, an acceptance the
 * customer returned, a plain attachment. It is never sent round to be signed
 * here, so the screen offers it none of the signing controls and groups it apart
 * from the sign-off documents. This is the single place that distinction is
 * decided; migration 0111 lets these kinds exist, and nothing else in the app
 * needs to know which kinds are in the set — only whether a given one is.
 */
export const RECEIVED_KINDS: readonly DocumentKind[] = ['sow', 'plan', 'acceptance', 'attachment'];

export const isReceived = (kind: DocumentKind): boolean =>
  (RECEIVED_KINDS as readonly string[]).includes(kind);

/**
 * WHICH DOCUMENT KINDS MAY *NOT* BE SHARED WITH A VENDOR — Feature B, and the ONE
 * source of truth for it. The rule Vinnie set (22 Oct 2026) is the other way round
 * from most access decisions: EVERY document kind may be made shareable (always off
 * by default), EXCEPT anything that touches budget or payments, which is locked
 * Internal and can never be turned on. So the spec, the test scripts, the charter,
 * the change requests, the sign-offs and the reference files are all a manager's to
 * share with the supplier doing the work; the money is not.
 *
 * Hence a BLOCK-list, not an allow-list. And today it is EMPTY: budget and payments
 * are their own screen (Budget & payments), not documents — no value of DocumentKind
 * above is a budget or payment document — so nothing is locked yet, and every kind
 * gets the share toggle. The list exists so that WHEN a budget- or payment-bearing
 * document kind is introduced, it is named here and is Internal from the first day.
 *
 * ⚠️ IF YOU ADD A KIND HERE, enforce it in the DATABASE too, not only the screen:
 * add it to the vendor clause of documents_read AND to the write trigger
 * record_document_sharing (both in a new migration), so a locked kind cannot be
 * served or flagged even by a direct write. The screen hiding the toggle is a
 * convenience; the row policy is the security.
 */
export const NEVER_SHAREABLE_WITH_VENDORS: readonly DocumentKind[] = [];

export const isVendorShareable = (kind: DocumentKind): boolean =>
  !(NEVER_SHAREABLE_WITH_VENDORS as readonly string[]).includes(kind);

/**
 * WHAT THE PROJECT IS AND WAS HANDED — the documents that belong on the Plan.
 * A received/reference file (a scope of work, a plan, an acceptance, an
 * attachment) and the charter: things the project IS, not things it sends out to
 * be signed. Everything else — change requests, deliverables, test scripts — is a
 * delivery artifact and lives on the Documents screen, summarised on the Plan.
 */
export const isProjectArtifact = (kind: DocumentKind): boolean =>
  isReceived(kind) || kind === 'charter';

/**
 * OPEN THE NEW-DOCUMENT FORM ON ARRIVAL. Drilling to the Documents screen with
 * this in place of a document id means "I came here to make one" — the Plan's
 * "New document" button sends it, and the Documents screen opens the form rather
 * than just landing the reader on the list. A real document id never looks like
 * this, so the reveal-a-document path is untouched.
 */
export const NEW_DOC = '__new__';

/**
 * What each state is called, written as the reader's question rather than as
 * the system's word — "Awaiting sign-off" says what is happening to it, where
 * `awaiting` alone reads as a category.
 */
export const DOCUMENT_STATE: Record<DocumentState, string> = {
  draft: 'Draft',
  awaiting: 'Awaiting sign-off',
  signed: 'Signed off',
  superseded: 'Superseded',
};

/**
 * WHERE A DOCUMENT ACTUALLY STANDS, rejection included — #signoff-r1.
 *
 * `DocumentState` has no "rejected": a refusal lives on the sign-off rows, not on
 * the document, so a rejected sheet leaves `doc.state` at `awaiting`. Read on its
 * own that is a lie — the header said "Awaiting sign-off" over a document somebody
 * had turned down, and offered its approved copy for download. This is the single
 * place that reconciles the two, so every screen says the same thing: a document
 * still awaiting, with a rejection against it, stands as "Not approved".
 *
 * Only `awaiting` is overridden. A signed or superseded document has moved on and
 * a stale rejected row on an earlier round must not drag it back.
 */
export function isRejected(
  doc: Doc,
  signoffs: { documentId?: string; answer?: 'approved' | 'rejected' }[]
): boolean {
  return doc.state === 'awaiting' && signoffs.some((r) => r.documentId === doc.id && r.answer === 'rejected');
}

/**
 * A DOCUMENT IS EDITED ONLY WHILE IT IS A DRAFT, and the database says so too
 * — migration 0044 stops matching any policy the moment it moves on. Asked
 * here as well so the screen does not offer a control the write will refuse.
 */
export const isEditable = (doc: Doc): boolean => doc.state === 'draft';

/** What it covers, for a reader: a sprint's name, or the whole project. */
export function covers(doc: Doc, sprintName: (id: string) => string | undefined): string {
  if (!doc.sprint) return 'Whole project';
  return sprintName(doc.sprint) ?? 'A sprint that is no longer here';
}

/**
 * THE HISTORY OF ONE DOCUMENT, newest first.
 *
 * A version is a new row pointing at what it replaced, so the chain is walked
 * rather than sorted. Guarded against a cycle: `supersedes` is written by this
 * app and should never loop, but a walk that trusts that is a walk that hangs
 * the screen if it is ever wrong.
 */
/**
 * ── A SPRINT SIGN-OFF PREPARED BUT NOT SENT ─────────────────────────────────
 *
 * A sprint carries two sign-off documents — the change request that authorises
 * the work at planning, and the deliverables record signed at the end. Either
 * can be drafted and left unsent, and until it is sent the sprint is not signed
 * off at that phase — which Vinnie asked to persist rather than be forgotten.
 * A sent sheet already counts as outstanding through its signatures; a DRAFT has
 * none, so it would vanish from every count. This is that gap: the authorisation
 * or deliverables documents for a sprint that are still drafts.
 *
 * The inbox and the nav badge both read this, so the number the badge shows and
 * the rows the inbox lists cannot drift apart.
 */
export function preparedSprintSignoffs(
  documents: Doc[],
  sprints: { id: string; state: string }[]
): Doc[] {
  const known = new Set(sprints.map((s) => s.id));
  return documents.filter(
    (d) =>
      (d.kind === 'change' || d.kind === 'deliverables') &&
      d.state === 'draft' &&
      !!d.sprint &&
      known.has(d.sprint)
  );
}

export function versionsOf(doc: Doc, all: Doc[]): Doc[] {
  const byId = new Map(all.map((d) => [d.id, d]));
  const chain: Doc[] = [];
  const seen = new Set<string>();
  let at: Doc | undefined = doc;
  while (at && !seen.has(at.id)) {
    seen.add(at.id);
    chain.push(at);
    at = at.supersedes ? byId.get(at.supersedes) : undefined;
  }
  return chain;
}

/**
 * WHAT MAY BE UPLOADED, and what happens to each when somebody has to read it.
 *
 * Vinnie's decision, 21 August, with the trade-off stated: a PDF is read on the
 * page, and a Word file has to be downloaded and opened in something else. Both
 * are accepted; the screen says which this one is rather than implying that
 * both can be read where they sit. Kept in step with the bucket's own list in
 * migration 0048 — the database is what actually refuses, this only decides
 * what the file picker offers and what the message says.
 */
/**
 * EVERY FILE TYPE THE STORE CAN HOLD, and whether it can be read where it sits.
 *
 * `readable` means there is an in-app view for it — a PDF and an image are shown
 * on the page, a CSV is drawn as a table, and everything else is downloaded and
 * opened in its own app. Kept in step with the bucket's own allow-list across
 * migrations 0048 (PDF, Word), 0065 (PNG, JPEG) and 0110 (CSV, Excel) — the
 * database is what actually refuses; this decides what the picker offers, what
 * the meta line says, and how a kept file is opened.
 */
const FILE_TYPE: Record<string, { what: string; readable: boolean }> = {
  'application/pdf': { what: 'PDF', readable: true },
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': {
    what: 'Word document',
    readable: false,
  },
  'application/msword': { what: 'Word document', readable: false },
  'text/csv': { what: 'CSV', readable: true },
  // Some systems label a .csv as ms-excel; either way an .xls is a spreadsheet
  // there is no in-app view for, so it downloads.
  'application/vnd.ms-excel': { what: 'Excel', readable: false },
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': { what: 'Excel', readable: false },
  'image/png': { what: 'PNG image', readable: true },
  'image/jpeg': { what: 'JPEG image', readable: true },
};

/**
 * WHAT MAY BE UPLOADED TO A SIGNABLE DOCUMENT — a PDF or a Word file, and no
 * more. Vinnie's decision, 21 August: the person asked to sign has to be able to
 * read what they are signing, so a spreadsheet or an image is not an acceptable
 * thing to send round for signature. A received document is not signed, so it
 * takes the wider set below.
 */
export const ACCEPTED: Record<string, { what: string; readable: boolean }> = {
  'application/pdf': FILE_TYPE['application/pdf'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
    FILE_TYPE['application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
  'application/msword': FILE_TYPE['application/msword'],
};

/**
 * WHAT MAY BE KEPT AS A RECEIVED DOCUMENT — the whole widened bucket. A project
 * is handed spreadsheets, PDFs, Word files and images and needs to keep any of
 * them for the record; none of these is ever sent for signature.
 */
export const RECEIVED_ACCEPTED: Record<string, { what: string; readable: boolean }> = FILE_TYPE;

/**
 * WHAT MAY BE UPLOADED TO A TEST SCRIPT — PDF or Word, AND Excel. A test script
 * is often a spreadsheet of cases, so Excel is accepted here (Vinnie, 15 Oct
 * 2026). An Excel test script IS sent for sign-off (Vinnie, 5 Nov 2026) through
 * the download → complete → upload → sign flow (signsByUpload), since a signatory
 * cannot read a spreadsheet in the page. This is the one produced kind that takes
 * a spreadsheet.
 */
export const TEST_ACCEPTED: Record<string, { what: string; readable: boolean }> = {
  ...ACCEPTED,
  'application/vnd.ms-excel': FILE_TYPE['application/vnd.ms-excel'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
    FILE_TYPE['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
};

/** The upload allow-list that applies to a document of this kind. */
export const acceptListFor = (
  kind: DocumentKind
): Record<string, { what: string; readable: boolean }> =>
  isReceived(kind) ? RECEIVED_ACCEPTED : kind === 'test' ? TEST_ACCEPTED : ACCEPTED;

/** The file picker's `accept` list for a document of this kind. */
export const acceptFor = (kind: DocumentKind): string => Object.keys(acceptListFor(kind)).join(',');

/**
 * A stored file a signatory is asked to read AS-IS — a PDF or a Word document.
 * These are signed the ordinary way: read in the page (PDF) or downloaded (Word),
 * then signed, with no step that changes the file. An Excel is not one of these.
 */
export const isSignableFile = (doc: Doc): boolean => !!doc.fileType && !!ACCEPTED[doc.fileType];

/** A spreadsheet file type (Excel .xls / .xlsx) — no in-page view, so it downloads. */
export const isSpreadsheet = (fileType: string | undefined): boolean =>
  fileType === 'application/vnd.ms-excel' ||
  fileType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/**
 * A TEST SCRIPT held as a spreadsheet, which is signed off through the
 * DOWNLOAD → COMPLETE → UPLOAD → SIGN flow rather than read in the page (Vinnie,
 * 5 Nov 2026). The tester runs the cases offline, uploads the completed workbook,
 * and that completed workbook becomes the version everyone else signs.
 */
export const signsByUpload = (doc: Doc): boolean =>
  doc.kind === 'test' && isSpreadsheet(doc.fileType);

/**
 * A stored file that can be SENT FOR SIGN-OFF. A PDF or Word goes the ordinary
 * way (isSignableFile); a TEST SCRIPT may also be an Excel, sent through the
 * complete-and-upload flow (signsByUpload) — this reverses the 15 Oct 2026 rule
 * that an Excel was kept for the record but never sent. Every other kind still
 * needs a readable PDF/Word file, so the signatory can read what they sign.
 */
export const sendableForSignoff = (doc: Doc): boolean =>
  isSignableFile(doc) || signsByUpload(doc);

/** What a stored file is called, for the meta line — 'PDF', 'Excel', 'PNG image'. */
export const fileWhat = (fileType: string | undefined): string =>
  (fileType && FILE_TYPE[fileType]?.what) || '';

/** 25 MB — see the bucket in 0048, which is what actually enforces it. */
export const BIGGEST = 26214400;

/** Read where it sits (PDF, image, CSV) rather than downloaded (Word, Excel). */
export const readsOnThePage = (doc: Doc): boolean =>
  !!doc.fileType && FILE_TYPE[doc.fileType]?.readable === true;

/** How a readable file is drawn in the in-app viewer. */
export const viewAs = (fileType: string | undefined): 'pdf' | 'image' | 'table' | null => {
  if (!fileType) return null;
  if (fileType === 'application/pdf') return 'pdf';
  if (fileType === 'image/png' || fileType === 'image/jpeg') return 'image';
  if (fileType === 'text/csv') return 'table';
  return null;
};

/**
 * WHY THIS FILE CANNOT BE TAKEN, or empty if it can.
 *
 * Asked before anything is uploaded so the reason is a sentence rather than a
 * refusal from the bucket, which arrives as a status code. The bucket still
 * refuses independently — this is the courtesy, not the control. A received
 * document takes the wider set; a signable one is held to PDF or Word.
 */
export function whyNot(
  file: { type: string; size: number; name: string },
  received = false,
  kind?: DocumentKind
): string {
  const list = kind ? acceptListFor(kind) : received ? RECEIVED_ACCEPTED : ACCEPTED;
  if (!list[file.type]) {
    if (list === RECEIVED_ACCEPTED)
      return `${file.name} is not a type we can keep. Accepted: PDF, Word, Excel, CSV, or an image.`;
    if (list === TEST_ACCEPTED)
      return `${file.name} is not a PDF, a Word document or an Excel file.`;
    return `${file.name} is not a PDF or a Word document. A signatory has to be able to read what they are signing.`;
  }
  if (file.size > BIGGEST)
    return `${file.name} is ${Math.round(file.size / 1048576)} MB, and the limit is 25 MB.`;
  if (file.size === 0) return `${file.name} is empty.`;
  return '';
}

/**
 * The object key: board, then document, then the name.
 *
 * THE ORDER IS LOAD-BEARING. A storage rule can only judge what it can read out
 * of the key, so the board has to come first or the policies in 0048 have
 * nothing to ask about. The name is cleaned because it ends up in a URL and
 * because somebody's file is called "SOW (final) v2 — signed.pdf".
 */
export const fileKey = (boardId: string, docId: string, name: string): string =>
  `${boardId}/${docId}/${name.replace(/[^\w.\- ]+/g, '_').replace(/\s+/g, '_').slice(-120)}`;

/**
 * HOW LONG A SIGNING LINK LASTS — #216, Vinnie's call on 22 August.
 *
 * Two weeks: long enough for somebody on leave, short enough that a forwarded
 * email next quarter is dead. It is not a session and not a password reset —
 * it is a request for an answer, and a request that is still open a month later
 * has been forgotten rather than delayed.
 */
export const FOR_A_FORTNIGHT = 14 * 24 * 60 * 60 * 1000;

/**
 * WHAT A FILE ACTUALLY IS — sha256 of its bytes, taken in the browser as it
 * goes up.
 *
 * A signature is against a document, not against a title. Recording this at
 * upload is what lets a signing link be issued AGAINST something: if the file
 * is ever replaced, the fingerprint no longer matches and the page an approver
 * opens refuses to be signed rather than collecting an approval for a file
 * nobody read.
 *
 * Taken here rather than on a server because the bytes are here already — and
 * because a fingerprint the server computes from what it was sent proves only
 * what it was sent.
 */
export async function fingerprint(file: Blob): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}
